import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useStorage, useDebounceFn } from "@vueuse/core"
import { extractDate } from "@/utils/datetime.js"
import { useSupplierProductsImportListStore } from "@/stores/storage/SupplierProductsImportListStore"
import { useRouter } from "vue-router"

/**
 * Import list type constants matching API values
 * @constant {Object} IMPORT_LIST_TYPE
 */
export const IMPORT_LIST_TYPE = {
  NONE: 0,
  SALE: 1,
  USAGE: 2,
  BOTH: 3,
}


/**
 * Supplier Products Store
 *
 * Manages supplier products data for import functionality including:
 * - Fetching products list with pagination, filtering and sorting
 * - Managing filters (producers, categories)
 * - Tracking products with changed states across pagination pages
 * - Bulk state update operations
 */
export const useSupplierProductsStore = defineStore('supplierProductsStore', () => {

  // ========================================
  // State
  // ========================================
  const importListStore = useSupplierProductsImportListStore()

  /** Current supplier ID */
  const currentSupplierId = ref(null)

  /** Global loading state for products operations */
  const isLoading = ref(true)

  /** Loading state for filters */
  const isLoadingFilters = ref(false)

  /** Loading state for state update operations */
  const isUpdatingStates = ref(false)

  const router = useRouter()

  /**
   * Set of product IDs currently being updated (for individual product updates)
   * @type {Set<number>}
   */
  const updatingProductIds = ref(new Set())

  /**
   * Main data object containing products list and pagination info
   * @property {boolean} isLoaded - Whether the data has been fetched
   * @property {Array} products - List of product objects
   * @property {Object} meta - Pagination metadata (total, perPage, page)
   */
  const productsData = ref({
    isLoaded: false,
    products: [],
    meta: {
      total: 0,
      perPage: 11,
      page: 1,
      totalAll: 0,
    },
  })

  /**
   * Available filters from API
   * @property {Array<{id: string, name: string}>} producers - List of producer objects
   * @property {Array<{id: string, name: string}>} categories - List of category objects
   */
  const availableFilters = ref({
    producers: [],
    categories: [],
  })

  /** Search query for filtering products */
  const searchQuery = useStorage('storage-import-search', '', localStorage, {
    listenToStorageChanges: false,
  })

  /** Selected producer filter */
  const selectedProducer = useStorage('storage-import-selected-producer', null, localStorage, {
    listenToStorageChanges: false,
  })

  /** Selected category filter */
  const selectedCategory = useStorage('storage-import-selected-category', null, localStorage, {
    listenToStorageChanges: false,
  })

  /**
   * Sorting configuration
   * @property {string} column - Column to sort by (producer, category, name, ean, buyPrice, isNew)
   * @property {string} direction - Sort direction (asc, desc)
   */
  const sorting = ref({
    column: null,
    direction: 'asc',
  })

  /**
   * Original product states from API (inImportListType)
   * Key: product ID, Value: original inImportListType
   * Used to track which products have changed state
   * @type {Map<number, number>}
   */
  const originalProductStates = ref(new Map())

  /**
   * Current import list type selection per product
   * Key: product ID, Value: importListType (0, 1, 2, or 3)
   * Tracks the current selected state for each product
   * @type {Map<number, number>}
   */
  const productImportListTypeSelections = ref(new Map())

  // ========================================
  // Getters
  // ========================================

  /**
   * Total count of products in import list (all, regardless of filters)
   * @returns {number}
   */
  const totalProductsCount = computed(() => productsData.value.meta.totalAll)

  /**
   * Filtered products count (matching current filters)
   * @returns {number}
   */
  const filteredProductsCount = computed(() => productsData.value.meta.total)

  /**
   * Computed property for total pages based on pagination metadata
   * @returns {number} Total number of pages
   */
  const totalPages = computed(() => {
    const { total, perPage } = productsData.value.meta

    return Math.ceil(total / perPage) || 1
  })

  /**
   * Computed property for current page
   * @returns {number} Current page number
   */
  const currentPage = computed(() => productsData.value.meta.page)

  /**
   * Computed counters for products with changed states (grouped by new state)
   * Only counts products where current selection differs from original state
   * @returns {Object} Counters for usage, sale, and both
   */
  const changedStateCounters = computed(() => {
    let usage = 0
    let sale = 0
    let both = 0
    let none = 0

    productImportListTypeSelections.value.forEach((currentType, productId) => {
      const originalType = originalProductStates.value.get(productId) ?? IMPORT_LIST_TYPE.NONE

      // Only count if state has changed
      if (currentType !== originalType) {
        if (currentType === IMPORT_LIST_TYPE.USAGE) usage++
        else if (currentType === IMPORT_LIST_TYPE.SALE) sale++
        else if (currentType === IMPORT_LIST_TYPE.BOTH) both++
        else if (currentType === IMPORT_LIST_TYPE.NONE) none++
      }
    })

    return { usage, sale, both, none, total: usage + sale + both + none }
  })

  /**
   * Check if any products have changed states
   * @returns {boolean}
   */
  const hasChangedProducts = computed(() => changedStateCounters.value.total > 0)

  /**
   * Get all products with changed states
   * @returns {Array<{supplierProductId: number, newType: number, originalType: number}>}
   */
  const getChangedProducts = computed(() => {
    const changed = []

    productImportListTypeSelections.value.forEach((currentType, productId) => {
      const originalType = originalProductStates.value.get(productId) ?? IMPORT_LIST_TYPE.NONE

      if (currentType !== originalType) {
        changed.push({
          supplierProductId: productId,
          newType: currentType,
          originalType: originalType,
        })
      }
    })

    return changed
  })

  // ========================================
  // Actions
  // ========================================

  /**
   * Fetch available filters for a supplier
   *
   * @param {number} supplierId - Supplier ID
   * @returns {Promise<boolean>} True if successful, false otherwise
   */
  const fetchFilters = async (supplierId) => {
    isLoadingFilters.value = true

    try {
      const response = await $backend(`/supplierProducts/filters?supplierId=${supplierId}`, {
        method: 'GET',
      })

      if (response.success) {
        availableFilters.value = {
          producers: response.data.filters.producers || [],
          categories: response.data.filters.categories || [],
        }

        return true
      }
    } catch (error) {
      console.error('Error fetching supplier products filters:', error)
      return false
    } finally {
      isLoadingFilters.value = false
    }

    return false
  }

  /**
   * Refetch products list for the current supplier
   *
   * @returns {Promise<boolean>} True if successful, false otherwise
   */
  const refetchProducts = async () => {
    if (!currentSupplierId.value) return false
    await fetchProducts(currentSupplierId.value, {
      page: productsData.value.meta.page,
      perPage: productsData.value.meta.perPage,
      force: true,
    })
  }

  /**
   * Fetch products list from the backend
   *
   * @param {number} supplierId - Supplier ID
   * @param {Object} options - Fetch options
   * @param {number} options.page - Page number (default: 1)
   * @param {number} options.perPage - Items per page (default: 11)
   * @param {boolean} options.force - Force refetch even if loaded
   * @returns {Promise<boolean>} True if successful, false otherwise
   */
  const fetchProducts = async (supplierId, { page = 1, perPage = 11, force = false } = {}) => {
    // Skip fetching if data is already loaded for the same supplier and page
    if (
      !force &&
      productsData.value.isLoaded &&
      currentSupplierId.value === supplierId &&
      productsData.value.meta.page === page
    ) {
      return true
    }

    isLoading.value = true
    currentSupplierId.value = supplierId

    try {
      // Build query parameters
      const params = new URLSearchParams({
        supplierId: supplierId.toString(),
        page: page.toString(),
        perPage: perPage.toString(),
      })

      // Add sorting if specified
      if (sorting.value.column) {
        params.append('orders[0][column]', sorting.value.column)
        params.append('orders[0][direction]', sorting.value.direction)
      }

      // Add filters if specified
      if (searchQuery.value.trim()) {
        params.append('filters[search]', searchQuery.value.trim())
      }
      if (selectedProducer.value) {
        params.append('filters[producer]', selectedProducer.value)
      }
      if (selectedCategory.value) {
        params.append('filters[category]', selectedCategory.value)
      }

      const response = await $backend(`/supplierProducts/index?${params.toString()}`, {
        method: 'GET',
      })

      if (response.success) {
        // If we are on far away page and there is no results for this page we go to last page instead
        if (response.data.products.length === 0 && page !== 1 && response.meta?.total > 0) {
          productsData.value.isLoaded = false
          const lastPage = Math.ceil(response.meta.total / (response.meta?.perPage ?? perPage))
          await router.push({ path: `/storage/import/${supplierId}`, query: { page: lastPage } })

          return true
        }


        productsData.value.isLoaded = true
        productsData.value.products = response.data.products || []
        productsData.value.meta = response.meta || {
          total: 0,
          perPage: perPage,
          page: page,
        }

        importListStore.importListCounter = response.data.listCounter || 0

        // Store original states and sync current selections for loaded products
        syncProductStatesWithFetchedData()

        return true
      }
    } catch (error) {
      //console.error('Error fetching supplier products:', error)
      return false
    } finally {
      isLoading.value = false
    }

    return false
  }

  /**
   * Sync original states and current selections with fetched product data
   * - Stores original listType for each product (only if not already stored)
   * - Sets current selection to original state if not already modified
   */
  const syncProductStatesWithFetchedData = () => {
    productsData.value.products.forEach((product) => {
      const originalType = product.listType ?? IMPORT_LIST_TYPE.NONE

      // Store original state only if not already stored (to preserve across pagination)
      if (!originalProductStates.value.has(product.id)) {
        originalProductStates.value.set(product.id, originalType)
      }

      // Set current selection to match original if not already modified by user
      if (!productImportListTypeSelections.value.has(product.id)) {
        productImportListTypeSelections.value.set(product.id, originalType)
      }
    })
  }

  /**
   * Change the current page and refetch products
   *
   * @param {number} page - New page number
   * @returns {Promise<boolean>} True if successful, false otherwise
   */
  const changePage = async (page) => {
    if (!currentSupplierId.value) return false

    return await fetchProducts(currentSupplierId.value, {
      page,
      perPage: productsData.value.meta.perPage,
      force: true,
    })
  }

  /**
   * Change items per page and refetch products (reset to page 1)
   *
   * @param {number} perPage - Items per page
   * @returns {Promise<boolean>}
   */
  const changePerPage = async (perPage) => {
    if (!currentSupplierId.value) return false

    return await fetchProducts(currentSupplierId.value, {
      page: 1,
      perPage,
      force: true,
    })
  }

  /**
   * Set search query and refetch products (reset to page 1)
   *
   * @param {string} query - Search query
   * @returns {Promise<boolean>}
   */
  const setSearchQuery = async (query) => {
    searchQuery.value = query
    if (!currentSupplierId.value) return false

    return await fetchProducts(currentSupplierId.value, {
      page: 1,
      perPage: productsData.value.meta.perPage,
      force: true,
    })
  }

  /**
   * Debounced action: updates search query and refetches products after 300 ms of inactivity
   *
   * @param {string} query - Search query
   * @returns {Promise<boolean>}
   */
  const debouncedSetSearchQuery = useDebounceFn(async (query) => {
    searchQuery.value = query

    return await fetchProducts(currentSupplierId.value, {
      page: 1,
      perPage: productsData.value.meta.perPage,
      force: true,
    })
  }, 300)

  /**
   * Set producer filter and refetch products (reset to page 1)
   *
   * @param {string|null} producer - Producer name or null to clear
   * @returns {Promise<boolean>}
   */
  const setProducerFilter = async (producer) => {
    selectedProducer.value = producer
    if (!currentSupplierId.value) return false

    return await fetchProducts(currentSupplierId.value, {
      page: 1,
      perPage: productsData.value.meta.perPage,
      force: true,
    })
  }

  /**
   * Set category filter and refetch products (reset to page 1)
   *
   * @param {string|null} category - Category name or null to clear
   * @returns {Promise<boolean>}
   */
  const setCategoryFilter = async (category) => {
    selectedCategory.value = category
    if (!currentSupplierId.value) return false

    return await fetchProducts(currentSupplierId.value, {
      page: 1,
      perPage: productsData.value.meta.perPage,
      force: true,
    })
  }

  /**
   * Set sorting and refetch products (reset to page 1)
   *
   * @param {string|null} column - Column to sort by
   * @param {string} direction - Sort direction (asc/desc)
   * @returns {Promise<boolean>}
   */
  const setSorting = async (column, direction = 'asc') => {
    sorting.value = { column, direction }
    if (!currentSupplierId.value) return false

    return await fetchProducts(currentSupplierId.value, {
      page: 1,
      perPage: productsData.value.meta.perPage,
      force: true,
    })
  }

  /**
   * Clear all filters and reset to initial state
   *
   * @returns {Promise<boolean>}
   */
  const clearAllFilters = async () => {
    searchQuery.value = ''
    selectedProducer.value = null
    selectedCategory.value = null
    sorting.value = { column: null, direction: 'asc' }

    if (!currentSupplierId.value) return false

    return await fetchProducts(currentSupplierId.value, {
      page: 1,
      perPage: productsData.value.meta.perPage,
      force: true,
    })
  }

  /**
   * Set import list type selection for a product
   *
   * @param {number} productId - Product ID
   * @param {number} importListType - Import list type (IMPORT_LIST_TYPE.NONE, SALE, USAGE, or BOTH)
   */
  const setProductImportListType = (productId, importListType) => {
    productImportListTypeSelections.value.set(productId, importListType)
  }

  /**
   * Get import list type selection for a product
   *
   * @param {number} productId - Product ID
   * @returns {number} Import list type or IMPORT_LIST_TYPE.NONE
   */
  const getProductImportListType = (productId) => {
    return productImportListTypeSelections.value.get(productId) ?? IMPORT_LIST_TYPE.NONE
  }

  /**
   * Get original import list type for a product (from API)
   *
   * @param {number} productId - Product ID
   * @returns {number} Original import list type or IMPORT_LIST_TYPE.NONE
   */
  const getOriginalProductImportListType = (productId) => {
    return originalProductStates.value.get(productId) ?? IMPORT_LIST_TYPE.NONE
  }

  /**
   * Check if a product has changed state
   *
   * @param {number} productId - Product ID
   * @returns {boolean}
   */
  const hasProductChangedState = (productId) => {
    const currentType = getProductImportListType(productId)
    const originalType = getOriginalProductImportListType(productId)

    return currentType !== originalType
  }

  /**
   * Toggle a specific import list type for a product
   * If the same type is already selected, deselect it (set to NONE)
   * If a different type is selected, switch to the new type
   *
   * @param {number} productId - Product ID
   * @param {number} importListType - Import list type to toggle
   */
  const toggleProductImportListType = (productId, importListType) => {
    const currentType = getProductImportListType(productId)

    if (currentType === importListType) {
      // Deselect if clicking the same type - set to NONE
      setProductImportListType(productId, IMPORT_LIST_TYPE.NONE)
    } else {
      // Select the new type
      setProductImportListType(productId, importListType)
    }
  }

  /**
   * Set all visible products on current page to a specific import list type
   *
   * @param {number} importListType - Import list type to set for all products
   */
  const setAllVisibleProductsImportListType = (importListType) => {
    productsData.value.products.forEach((product) => {
      setProductImportListType(product.id, importListType)
    })
  }

  /**
   * Set import list type for all products that have changed states
   * (products where current selection differs from original state)
   *
   * @param {number} importListType - Import list type to set for changed products
   */
  const setChangedProductsImportListType = (importListType) => {
    const changedProducts = getChangedProducts.value
    changedProducts.forEach(({ supplierProductId }) => {
      setProductImportListType(supplierProductId, importListType)
    })
  }

  /**
   * Check if all visible products have a specific import list type
   *
   * @param {number} importListType - Import list type to check
   * @returns {boolean}
   */
  const areAllVisibleProductsOfType = (importListType) => {
    if (productsData.value.products.length === 0) return false

    return productsData.value.products.every((product) => {
      return getProductImportListType(product.id) === importListType
    })
  }

  /**
   * Check if a product is currently being updated
   *
   * @param {number} productId - Product ID
   * @returns {boolean}
   */
  const isProductUpdating = (productId) => {
    return updatingProductIds.value.has(productId)
  }

  /**
   * Check if any of products is currently being updated
   * @returns {boolean}
   */
  const isAnyProductUpdatingNow = computed( () => {
    return updatingProductIds.value.size > 0 // We are working with Set
  })

  /**
   * Add a single product to import list (when product is not in import list yet)
   *
   * @param {number} productId - Product ID
   * @returns {Promise<boolean>}
   */
  const addProductToImportList = async (productId) => {
    const currentType = getProductImportListType(productId)

    if (currentType === IMPORT_LIST_TYPE.NONE) return false
    if (updatingProductIds.value.has(productId)) return false

    updatingProductIds.value.add(productId)

    try {
      const productToAdd = { supplierProductId: productId, newType: currentType }
      console.log('Adding product to import list:', productToAdd)

      const response = await $backend('/supplierProductsList/update', {
        method: 'POST',
        body: [productToAdd],
      })

      if (response && response.success) {
        // Update original state to match current selection (simulating successful API response)
        originalProductStates.value.set(productId, currentType)

        // Update the product object in the list if present
        const product = productsData.value.products.find(p => p.id === productId)
        if (product) {
          product.listType = currentType
          product.isOnList = true
        }

        importListStore.importListCounter = response.data.listCounter || 0

        return true
      }

      return false
    } catch (error) {
      //console.error('Error adding product to import list:', error)

      return false
    } finally {
      updatingProductIds.value.delete(productId)
    }
  }

  /**
   * Update a single product state in import list (when product is already in import list)
   *
   * @param {number} productId - Product ID
   * @returns {Promise<boolean>}
   */
  const updateProductState = async (productId) => {
    const currentType = getProductImportListType(productId)
    const originalType = getOriginalProductImportListType(productId)

    if (currentType === originalType) return false
    if (updatingProductIds.value.has(productId)) return false

    updatingProductIds.value.add(productId)

    try {
      const productToUpdate = { supplierProductId: productId, newType: currentType, originalType: originalType }
      console.log('Updating product state:', productToUpdate)

      //await new Promise(resolve => setTimeout(resolve, 500))
      const response = await $backend('/supplierProductsList/update', {
        method: 'POST',
        body: [productToUpdate],
      })

      if (response && response.success) {
        // Update original state to match current selection (simulating successful API response)
        originalProductStates.value.set(productId, currentType)

        // Update the product object in the list if present
        const product = productsData.value.products.find(p => p.id === productId)
        if (product) {
          product.listType = currentType
          product.isOnList = currentType !== IMPORT_LIST_TYPE.NONE
        }

        importListStore.importListCounter = response.data.listCounter || 0

        return true
      }

      return false
    } catch (error) {
      //console.error('Error updating product state:', error)

      return false
    } finally {
      updatingProductIds.value.delete(productId)
    }
  }

  /**
   * Remove a single product from import list
   *
   * @param {number} productId - Product ID
   * @returns {Promise<boolean>}
   */
  const removeProductFromImportList = async (productId) => {
    if (updatingProductIds.value.has(productId)) return false

    updatingProductIds.value.add(productId)

    try {
      const productToRemove = { supplierProductId: productId }
      console.log('Removing product from import list:', productToRemove)

      const response = await $backend('/supplierProductsList/delete', {
        method: 'POST',
        body: [productToRemove],
      })

      if (response && response.success) {
        // Update original state to NONE (simulating successful API response)
        originalProductStates.value.set(productId, IMPORT_LIST_TYPE.NONE)

        // Update current selection to NONE as well
        productImportListTypeSelections.value.set(productId, IMPORT_LIST_TYPE.NONE)

        // Update the product object in the list if present
        const product = productsData.value.products.find(p => p.id === productId)
        if (product) {
          product.listType = IMPORT_LIST_TYPE.NONE
          product.isOnList = false
        }

        importListStore.importListCounter = response.data.listCounter || 0

        return true
      }

      return false
    } catch (error) {
      //console.error('Error removing product from import list:', error)

      return false
    } finally {
      updatingProductIds.value.delete(productId)
    }
  }

  /**
   * Remove multiple products from import list (batch operation)
   *
   * @param {Array<number>} productIds - Array of supplier product IDs
   * @returns {Promise<boolean>}
   */
  const removeMultipleProductsFromImportList = async (productIds) => {
    if (!productIds || productIds.length === 0) return false

    try {
      const productsToRemove = productIds.map(id => ({ supplierProductId: id }))
      console.log('Removing multiple products from import list:', productsToRemove)

      const response = await $backend('/supplierProductsList/delete', {
        method: 'POST',
        body: productsToRemove,
      })

      if (response && response.success) {
        // Update local state for all removed products
        productIds.forEach(productId => {
          originalProductStates.value.set(productId, IMPORT_LIST_TYPE.NONE)
          productImportListTypeSelections.value.set(productId, IMPORT_LIST_TYPE.NONE)

          // Update the product object in the list if present
          const product = productsData.value.products.find(p => p.id === productId)
          if (product) {
            product.listType = IMPORT_LIST_TYPE.NONE
            product.isOnList = false
          }
        })
        importListStore.importListCounter = response.data.listCounter || 0

        return true
      }

      return false
    } catch (error) {
      //console.error('Error removing multiple products from import list:', error)

      return false
    }
  }

  /**
   * Update all changed product states (bulk operation to API)
   *
   * @returns {Promise<boolean>}
   */
  const updateAllChangedStates = async () => {
    const changedProducts = getChangedProducts.value

    if (changedProducts.length === 0) return false

    isUpdatingStates.value = true

    try {
      // API call to update original states to match current selections
      //await new Promise(resolve => setTimeout(resolve, 500))
      const response = await $backend('/supplierProductsList/update', {
        method: 'POST',
        body: changedProducts,
      })

      if(response && response.success) {
        changedProducts.forEach(({ supplierProductId, newType }) => {
          originalProductStates.value.set(supplierProductId, newType)

          // Also update the product object in the list if present
          const product = productsData.value.products.find(p => p.id === supplierProductId)
          if (product) {
            product.listType = newType
            product.isOnList = newType !== IMPORT_LIST_TYPE.NONE
          }
        })

        importListStore.importListCounter = response.data.listCounter || 0

        return true
      }

      return false
    } catch (error) {
      //
      return false
    } finally {
      isUpdatingStates.value = false
    }
  }

  /**
   * Add all supplier products to import list by filter (current supplier)
   * POST /supplierProductsList/updateByFilter
   *
   * @param {number} importListType - Import list type (IMPORT_LIST_TYPE.SALE, USAGE, or BOTH)
   * @param {Boolean} producer
   * @param {Boolean} category
   * @returns {Promise<boolean>}
   */
  const addAllSupplierProductsByFilter = async (importListType, producer = false, category = false) => {
    if (!currentSupplierId.value) return false

    isUpdatingStates.value = true

    try {
      const response = await $backend('/supplierProductsList/updateByFilter', {
        method: 'POST',
        body: {
          supplierId: currentSupplierId.value,
          producer: producer ? selectedProducer.value : null,
          category: category ? selectedCategory.value : null,
          type: importListType,
        },
      })

      if (response && response.success) {
        importListStore.importListCounter = response.data.listCounter ?? importListStore.importListCounter

        return true
      }

      return false
    } catch {
      return false
    } finally {
      isUpdatingStates.value = false
    }
  }

  /**
   * Switch to a different supplier - clears all state
   *
   * @param {number} supplierId - New supplier ID
   * @returns {Promise<boolean>}
   */
  const switchSupplier = async (supplierId) => {
    // Clear all state
    searchQuery.value = ''
    selectedProducer.value = null
    selectedCategory.value = null
    sorting.value = { column: null, direction: 'asc' }
    originalProductStates.value.clear()
    productImportListTypeSelections.value.clear()
    productsData.value = {
      isLoaded: false,
      products: [],
      meta: { total: 0, perPage: 10, page: 1 },
    }
    availableFilters.value = { producers: [], categories: [] }

    // Fetch new data
    currentSupplierId.value = supplierId
    await fetchFilters(supplierId)

    return await fetchProducts(supplierId, { page: 1, force: true })
  }

  /**
   * Reset store to initial state
   */
  const resetStore = () => {
    currentSupplierId.value = null
    isLoading.value = false
    isLoadingFilters.value = false
    isUpdatingStates.value = false
    productsData.value = {
      isLoaded: false,
      products: [],
      meta: { total: 0, perPage: 10, page: 1 },
    }
    availableFilters.value = { producers: [], categories: [] }
    // Persisted filters are intentionally NOT cleared here
    // (they survive navigation, just like in other storage stores).
    // Use clearAllFilters() for user-initiated clearing.
    sorting.value = { column: null, direction: 'asc' }
    originalProductStates.value.clear()
    productImportListTypeSelections.value.clear()
  }

  // ========================================
  // Exports
  // ========================================

  return {
    // State
    currentSupplierId,
    isLoading,
    isLoadingFilters,
    isUpdatingStates,
    updatingProductIds,
    productsData,
    availableFilters,
    searchQuery,
    selectedProducer,
    selectedCategory,
    sorting,
    originalProductStates,
    productImportListTypeSelections,

    // Getters
    totalPages,
    currentPage,
    changedStateCounters,
    hasChangedProducts,
    getChangedProducts,
    totalProductsCount,
    filteredProductsCount,

    // Actions
    fetchFilters,
    fetchProducts,
    refetchProducts,
    changePage,
    changePerPage,
    setSearchQuery,
    debouncedSetSearchQuery,
    setProducerFilter,
    setCategoryFilter,
    setSorting,
    clearAllFilters,
    setProductImportListType,
    getProductImportListType,
    getOriginalProductImportListType,
    hasProductChangedState,
    toggleProductImportListType,
    setAllVisibleProductsImportListType,
    setChangedProductsImportListType,
    areAllVisibleProductsOfType,
    isProductUpdating,
    isAnyProductUpdatingNow,
    addProductToImportList,
    updateProductState,
    removeProductFromImportList,
    removeMultipleProductsFromImportList,
    updateAllChangedStates,
    addAllSupplierProductsByFilter,
    switchSupplier,
    resetStore,
  }
})
